// Percorso: src/pages/enrolAiuto.jsx
//
// L'INTERRUTTORE DI LINGUA E LA RIGA D'AIUTO delle tre pagine pubbliche
// (cantiere «enrollment», cantiere 2).
//
// Le parole stanno in `enrolLingue.js` (una sola fonte, generata dal
// dizionario congelato). Qui c'è il MECCANISMO: chi tiene la lingua
// scelta, il comando che l'accende e la spegne, e il componente che
// disegna la riga d'aiuto sotto l'inglese.
//
// ⚠️ L'INGLESE È LA PAGINA. L'aiuto non sostituisce niente: è una riga
// IN PIÙ sotto l'etichetta inglese, che resta dov'è, com'è. Chi non
// tocca l'interruttore vede esattamente la pagina di prima — stesse
// parole, stesso ordine, e nemmeno un byte di carattere in più.
//
// ⚠️ NIENTE «English» FRA LE VOCI. L'interruttore accende e spegne un
// AIUTO: non sceglie fra quattro lingue. Ritoccare la lingua accesa la
// spegne e si torna al solo inglese.
//
// ⚠️ NESSUNA FUNZIONE DELLE PAGINE PASSA DI QUI. Questo file non
// valida, non naviga, non chiama il server e non tocca nessun dato:
// legge una stringa dal dizionario e la disegna.

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { LINGUE, CODICI_LINGUA, aiuto as rigaTradotta, testo } from "./enrolLingue";
import "./enrolAiuto.css";

/**
 * DOVE SI RICORDA LA LINGUA SCELTA.
 *
 * ⚠️ SI USA `localStorage`, e non è un'introduzione: queste stesse
 * pagine ci parcheggiano già l'indirizzo del link d'accesso
 * (`CHIAVE_EMAIL_ACCESSO` in Login.jsx, letta da FinishLogin.jsx). Il
 * motivo è che /login e /enrol sono due rotte diverse, e il rimando
 * «Not enrolled yet? ENROL NOW» è un <a> che RICARICA la pagina: senza
 * una sede fuori da React, chi ha scelto l'arabo sul login si
 * ritroverebbe la pagina d'iscrizione in solo inglese, cioè proprio
 * dove serviva di più.
 *
 * ⚠️ QUI DENTRO NON FINISCE NESSUN DATO PERSONALE: solo due lettere,
 * "ar", "zh" o "hi". Se il browser vieta la scrittura (navigazione
 * privata) non è un guaio: la scelta resta valida sulla pagina, e si
 * ripete alla successiva. Ogni accesso è protetto, come già fa Login.
 */
export const CHIAVE_LINGUA_AIUTO = "qpscHelpLanguage";

function lingueSalvata() {
  try {
    const salvata = window.localStorage.getItem(CHIAVE_LINGUA_AIUTO);
    return CODICI_LINGUA.includes(salvata) ? salvata : null;
  } catch (_) {
    return null;
  }
}

function salvaLingua(codice) {
  try {
    if (codice) window.localStorage.setItem(CHIAVE_LINGUA_AIUTO, codice);
    else window.localStorage.removeItem(CHIAVE_LINGUA_AIUTO);
  } catch (_) {
    /* localStorage non disponibile: si prosegue lo stesso. */
  }
}

/** null = nessun aiuto, cioè la pagina in solo inglese. */
const ContestoLingua = createContext({ lingua: null, scegli: () => {} });

/**
 * Il contenitore di stato. Va messo attorno all'ALBERO DELLA PAGINA, in
 * cima al componente di rotta: così l'interruttore e tutte le righe
 * d'aiuto della stessa pagina guardano la stessa scelta.
 */
export function ProvvistaLingua({ children }) {
  const [lingua, setLingua] = useState(lingueSalvata);

  // Un secondo tocco sulla lingua accesa la SPEGNE (si torna al solo
  // inglese): è l'unico modo per uscire, non essendoci una voce
  // «English» da premere.
  const scegli = useCallback((codice) => {
    setLingua((prima) => {
      const dopo = prima === codice ? null : codice;
      salvaLingua(dopo);
      return dopo;
    });
  }, []);

  const valore = useMemo(() => ({ lingua, scegli }), [lingua, scegli]);

  return <ContestoLingua.Provider value={valore}>{children}</ContestoLingua.Provider>;
}

/** La lingua d'aiuto attiva (o null) e il comando per cambiarla. */
export function useLinguaAiuto() {
  return useContext(ContestoLingua);
}

/**
 * L'INTERRUTTORE, in testa al guscio di ogni schermata pubblica.
 *
 * `avviso` accende la riga che spiega la divisione dei compiti — si
 * legge nella propria lingua, si scrive in inglese. Si passa sulle SOLE
 * DUE SCHERMATE D'INGRESSO del mockup approvato: l'accesso (/login,
 * prima dell'invio del link) e la scelta del percorso (/enrol). È lì che
 * si arriva da fuori e lì che l'avvertenza serve; da quel punto in poi —
 * moduli, conferme, «link inviato» — chi legge l'ha già letta, e
 * ripeterla su ogni schermata la trasformerebbe in rumore.
 */
export function BarraLingue({ avviso = false }) {
  const { lingua, scegli } = useLinguaAiuto();

  return (
    <div className="qpsc-en-lingue">
      <div
        className="qpsc-en-lingue__barra"
        role="group"
        aria-label={testo("lang.barLabel")}
      >
        {LINGUE.map((voce) => (
          <button
            key={voce.codice}
            type="button"
            className="qpsc-en-lingua"
            // `lang` non è decorazione: dice al browser in che lingua è
            // la parola (lettura ad alta voce, scelta del carattere) ed
            // è il gancio delle tre regole di carattere del foglio.
            lang={voce.codice}
            dir={voce.rtl ? "rtl" : "ltr"}
            aria-pressed={lingua === voce.codice}
            onClick={() => scegli(voce.codice)}
          >
            {voce.nome}
          </button>
        ))}
      </div>

      {avviso && (
        <p className="qpsc-en-lingue__avviso">
          {testo("lang.notice")}
          <Aiuto k="lang.notice" />
        </p>
      )}
    </div>
  );
}

/**
 * LA RIGA D'AIUTO. Si mette SUBITO SOTTO il testo inglese che traduce.
 *
 *   k        la chiave del dizionario
 *   valori   gli interpolati della chiave ({n}, {id}, {MAX})
 *   prefisso un'altra chiave da anteporre, per i messaggi composti
 *            («Child {n}: » + il motivo): è la stessa composizione
 *            dell'inglese, fatta nella lingua giusta
 *   colore   quale colore della sorgente prendere (vedi enrolAiuto.css);
 *            senza, resta il grigio del testo secondario
 *   centrato per l'aiuto sotto un pulsante a tutta colonna, che nel
 *            mockup è centrato come il pulsante che traduce
 *
 * Torna `null` — cioè non disegna niente — se non c'è lingua attiva,
 * se la chiave non esiste o se quella casella del dizionario è vuota.
 * Una traduzione mancante non lascia un buco: lascia il solo inglese.
 */
export function Aiuto({ k, valori, prefisso, colore, centrato = false }) {
  const { lingua } = useLinguaAiuto();
  if (!lingua) return null;

  const testa = prefisso ? rigaTradotta(prefisso.chiave, lingua, prefisso.valori) : "";
  const corpo = rigaTradotta(k, lingua, valori);
  if (!corpo) return null;

  const voce = LINGUE.find((l) => l.codice === lingua);
  const classi =
    "qpsc-en-aiuto" +
    (colore ? " qpsc-en-aiuto--" + colore : "") +
    (centrato ? " qpsc-en-aiuto--centrato" : "");

  return (
    <span
      className={classi}
      lang={lingua}
      dir={voce && voce.rtl ? "rtl" : "ltr"}
    >
      {testa + corpo}
    </span>
  );
}

/**
 * L'INGLESE DI UN AVVISO (errore di modulo o esito di una chiamata).
 *
 * ⚠️ GLI AVVISI SONO OGGETTI, NON STRINGHE, e questo è il punto del
 * refactor di questo cantiere. Prima il messaggio del minorenne veniva
 * confrontato PER TESTO, e bastava cambiargli una virgola per far
 * sparire in silenzio il varco «vai al modulo famiglia». Ora ogni
 * avviso porta la propria CHIAVE, che è la sua identità: il confronto
 * si fa su quella, e nessuna riscrittura di testo — né una traduzione —
 * può romperlo.
 *
 * Due forme sole:
 *   { chiave, valori?, prefisso? }  un avviso NOSTRO, dal dizionario
 *   { testo }                       le parole del SERVER, così come
 *                                   arrivano (vivono in server.js,
 *                                   fuori da questo perimetro: si
 *                                   mostrano in inglese e non si
 *                                   traducono)
 */
export function testoAvviso(avviso) {
  if (!avviso) return "";
  if (avviso.chiave) {
    const testa = avviso.prefisso
      ? testo(avviso.prefisso.chiave, avviso.prefisso.valori)
      : "";
    return testa + testo(avviso.chiave, avviso.valori);
  }
  return avviso.testo || "";
}

/**
 * L'avviso già vestito: la riga inglese e, sotto, il suo aiuto.
 * Sugli avvisi che arrivano dal server l'aiuto non c'è, e non è una
 * dimenticanza: quelle parole non stanno nel dizionario.
 */
export function RigaAvviso({ avviso, className, colore }) {
  if (!avviso) return null;

  return (
    <p className={className}>
      {testoAvviso(avviso)}
      {avviso.chiave && (
        <Aiuto
          k={avviso.chiave}
          valori={avviso.valori}
          prefisso={avviso.prefisso}
          colore={colore}
        />
      )}
    </p>
  );
}
